import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAppDispatch } from "../../../../store";
import { useSearchParams } from "react-router-dom";
import { Button, Select, Input, Spin, Modal, InputNumber } from "antd";
import { MinusOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { svg } from "../../../../Assets/svg";
import { RedStar } from "../../../../Components/Components";
import {
  selectEstimationDetails,
  setEstimationDetails,
  selectEstimationStepPlaylist,
  selectSelectedStorageTypes,
  calculateEstimationStepPlaylist,
} from "../estimationSlice";
import {
  selectStepPlaylist,
  setQuotationDetails,
} from "../../Quotations/quotationSlice";
import { updateQuotationAPI } from "../../Quotations/quotationApis";
import { updateEstimationAPI, calculateFloorCostAPI } from "../estimationApis";
import { formatCurrency, getFullAwsUrl } from "../../../../helpers/utils";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { getMezzanineMasterDataAPI } from "../../MasterData/mezzanineFloorSettingsApis";
import { CreateEstimationStepsEnum } from "../estimationTypes";
import { CreateQuotationStepsEnum } from "../../Quotations/quotationTypes";

interface FloorDetailsProps {
  readOnly?: boolean;
  data?: any;
  onSaveAndNext?: (values?: any) => void;
  onPrevious?: () => void;
}

const FloorDetails: React.FC<FloorDetailsProps> = ({ readOnly = false, data, onSaveAndNext, onPrevious }) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const estimationId = searchParams.get("estimationId");
  const quotationId =
    searchParams.get("quotationId") || searchParams.get("quoteId");
  const orderId = searchParams.get("orderId");
  const isQuoteMode = Boolean((quotationId || orderId) && !estimationId);
  const activeStep = searchParams.get("step") || "";

  const reduxEstimationDetails = useSelector(selectEstimationDetails);
  const quotationDetails = useSelector(
    (state: any) => state.quotation?.quotationDetails,
  );
  const reduxOrderDetails = useSelector(
    (state: any) => state.orders?.orderDetails,
  );
  const isEstimation = Boolean(estimationId || (!quotationId && !orderId && !reduxOrderDetails?._id));
  const quoteRequestDetails = useSelector(
    (state: any) => state.quoteRequest?.quoteRequestDetails,
  );
  const estimationDetails =
    data ||
    (isQuoteMode
      ? quotationDetails
      : reduxEstimationDetails || quotationDetails || reduxOrderDetails || quoteRequestDetails?.selected_change || quoteRequestDetails);

  const estimationStepPlaylist = useSelector(selectEstimationStepPlaylist);
  const quotationStepPlaylist = useSelector(selectStepPlaylist);
  const stepPlaylist = isQuoteMode
    ? quotationStepPlaylist
    : estimationStepPlaylist;

  const selectedStorageTypes = useSelector(selectSelectedStorageTypes);

  const [totalCost, setTotalCost] = useState<number | null>(null);
  const [loadingCost, setLoadingCost] = useState(false);

  const hasManufacturerPrice = useMemo(() => {
    if (!isQuoteMode) return false;
    if (estimationDetails?.has_manufacturer_price === true) return true;
    if (estimationDetails?.mezzanine_floor?.has_manufacturer_price === true) return true;
    if (quotationDetails?.has_manufacturer_price === true) return true;
    if (quotationDetails?.mezzanine_floor?.has_manufacturer_price === true) return true;
    return false;
  }, [isQuoteMode, estimationDetails, quotationDetails]);
  
  useEffect(() => {
    if (estimationDetails?.mezzanine_floor?.total_cost !== undefined && estimationDetails?.mezzanine_floor?.total_cost !== null) {
      setTotalCost(estimationDetails.mezzanine_floor.total_cost);
    }
  }, [estimationDetails?.mezzanine_floor?.total_cost]);

  // States for Master Settings
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [masterSettings, setMasterSettings] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const hasFetchedSettings = useRef(false);

  // Backup floor details modal state
  const [isBackupFloorModalOpen, setIsBackupFloorModalOpen] = useState(false);

  const isPendingManufacturerQuoteOrHigher = (status?: string): boolean => {
    if (!status) return false;
    const statusStr = status.toString().toLowerCase().trim();
    const allowed = [
      "pending-manufacturing-quote",
      "pending_manufacturing_quote",
      "pending manufacturing quote",
      "design-approval-require",
      "design_approval_require",
      "design approval require",
      "design approval required",
      "ready-for-quote",
      "ready_for_quote",
      "ready for quote",
      "quote-created",
      "quote_created",
      "quote created",
    ];
    return allowed.includes(statusStr);
  };

  // Helper functions to get settings names
  const getStaircaseName = (val: any) => {
    if (typeof val === "object") return val?.name || "-";
    const matched = masterSettings?.stairs?.find((s: any) => s._id === val);
    return matched ? matched.name : val || "-";
  };

  const getPalletGateName = (val: any) => {
    if (typeof val === "object") return val?.name || "-";
    const matched = masterSettings?.pallet_gates?.find((g: any) => g._id === val);
    return matched ? matched.name : val || "-";
  };

  const getFloorColourName = (val: any) => {
    if (typeof val === "object") return val?.name || "-";
    const matched = masterSettings?.floor_colours?.find((c: any) => c._id === val);
    return matched ? matched.name : val || "-";
  };

  const getGateColourName = (val: any) => {
    if (typeof val === "object") return val?.name || "-";
    const matched = masterSettings?.gate_colours?.find((c: any) => c._id === val);
    return matched ? matched.name : val || "-";
  };

  // 1. Fetch Mezzanine Master Data (once only)
  useEffect(() => {
    if (hasFetchedSettings.current) return;
    hasFetchedSettings.current = true;
    setLoadingSettings(true);
    dispatch(
      getMezzanineMasterDataAPI({
        onSuccess: (data) => {
          if (Array.isArray(data) && data.length > 0) {
            setMasterSettings(data[0]);
          } else if (data && !Array.isArray(data)) {
            setMasterSettings(data);
          }
          setLoadingSettings(false);
        },
        onError: () => {
          setLoadingSettings(false);
        },
      }),
    );
  }, [dispatch]);

  // 2. Identify the active category from the step key (e.g. categoryId__floor-details)
  const currentCategory = useMemo(() => {
    if (!activeStep.includes("__")) return null;
    const [categoryId] = activeStep.split("__");
    return selectedStorageTypes.find(
      (cat: any) => cat._id === categoryId || cat.category_id === categoryId,
    );
  }, [activeStep, selectedStorageTypes]);

  // Quantity Modal State
  const [quantityModal, setQuantityModal] = useState<{
    open: boolean;
    field: "staircase_type" | "pallet_gate_type" | null;
    pendingValue: string | null;
    initialValue: string | null;
    initialQty: number;
    isEditMode: boolean;
    title: string;
    itemName: string;
    qty: number;
  }>({
    open: false,
    field: null,
    pendingValue: null,
    initialValue: null,
    initialQty: 1,
    isEditMode: false,
    title: "",
    itemName: "",
    qty: 1,
  });

  const handleOpenQuantityModal = (
    field: "staircase_type" | "pallet_gate_type",
    selectedValue: any,
    isEditClick: boolean = false,
  ) => {
    if (!selectedValue) return;
    const targetId: string =
      typeof selectedValue === "object"
        ? selectedValue._id || selectedValue.id || ""
        : String(selectedValue);

    if (!targetId) return;

    const currentFormikVal = formik.values[field];
    const currentFormikId: string | null = currentFormikVal
      ? typeof currentFormikVal === "object"
        ? currentFormikVal._id || currentFormikVal.id || null
        : String(currentFormikVal)
      : null;

    const isAlreadySelected = Boolean(
      currentFormikId && currentFormikId === targetId,
    );
    const isEdit = isEditClick || isAlreadySelected;

    const name =
      field === "staircase_type"
        ? getStaircaseName(targetId)
        : getPalletGateName(targetId);
    const currentQty =
      field === "staircase_type"
        ? formik.values.staircase_type_qty || 1
        : formik.values.pallet_gate_type_qty || 1;

    // Immediately update Formik value so no "required" error flashes or displays
    formik.setFieldValue(field, targetId);
    formik.setFieldTouched(field, true, false);
    formik.setFieldError(field, undefined);

    setQuantityModal({
      open: true,
      field,
      pendingValue: targetId,
      initialValue: currentFormikId,
      initialQty: currentQty > 0 ? currentQty : 1,
      isEditMode: isEdit,
      title:
        field === "staircase_type"
          ? "Staircase Quantity"
          : "Pallet Gate Quantity",
      itemName: name,
      qty: currentQty > 0 ? currentQty : 1,
    });
  };

  const handleConfirmQuantityModal = () => {
    if (quantityModal.field && quantityModal.pendingValue) {
      const finalQty =
        quantityModal.qty > 0 ? Math.floor(quantityModal.qty) : 1;
      const fieldName = quantityModal.field;
      const qtyFieldName = `${quantityModal.field}_qty`;

      formik.setFieldValue(fieldName, quantityModal.pendingValue);
      formik.setFieldValue(qtyFieldName, finalQty);
      formik.setFieldTouched(fieldName, true, false);
      formik.setFieldError(fieldName, undefined);
    }
    setQuantityModal({
      open: false,
      field: null,
      pendingValue: null,
      initialValue: null,
      initialQty: 1,
      isEditMode: false,
      title: "",
      itemName: "",
      qty: 1,
    });
  };

  const handleCancelQuantityModal = () => {
    if (quantityModal.field) {
      const fieldName = quantityModal.field;
      const qtyFieldName = `${fieldName}_qty`;
      if (!quantityModal.isEditMode) {
        // If it was an unconfirmed new selection, revert to initialValue (or undefined)
        const prevVal = quantityModal.initialValue || undefined;
        const prevQty = quantityModal.initialQty || 1;
        formik.setFieldValue(fieldName, prevVal);
        formik.setFieldValue(qtyFieldName, prevQty);
        if (!prevVal) {
          formik.setFieldTouched(fieldName, true);
        }
      }
    }
    setQuantityModal({
      open: false,
      field: null,
      pendingValue: null,
      initialValue: null,
      initialQty: 1,
      isEditMode: false,
      title: "",
      itemName: "",
      qty: 1,
    });
  };

  // 3. Formik initial values from estimationDetails
  const initialValues = useMemo(() => {
    const existing = estimationDetails?.mezzanine_floor || {};
    const getID = (val: any) => {
      if (!val) return undefined;
      if (typeof val === "object") return val._id || val.id || undefined;
      return val;
    };
    return {
      square_metreage: existing.square_metreage ?? "",
      ffl: existing.ffl ?? "",
      staircase_type: getID(existing.staircase_type),
      staircase_type_qty: existing.staircase_type_qty ?? 1,
      pallet_gate_type: getID(existing.pallet_gate_type),
      pallet_gate_type_qty: existing.pallet_gate_type_qty ?? 1,
      total_length_of_handrail: existing.total_length_of_handrail ?? "",
      floor_colour: getID(existing.floor_colour),
      gate_colour: getID(existing.gate_colour),
    };
  }, [estimationDetails]);

  // 4. Validation Schema (memoized so it doesn't recreate on every render)
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        square_metreage: Yup.number()
          .typeError("Must be a number")
          .positive("Must be greater than 0")
          .required("Floor Square Metreage is required"),
        ffl: Yup.number()
          .typeError("Must be a number")
          .positive("Must be greater than 0")
          .required("Finish Floor Level is required"),
        staircase_type: Yup.string().required("Staircase Type is required"),
        pallet_gate_type: Yup.string().required("Pallet Gate Type is required"),
        total_length_of_handrail: Yup.number()
          .typeError("Must be a number")
          .min(0, "Cannot be less than 0")
          .required("Total Length of Handrail is required"),
        floor_colour: Yup.string().required("Floor Colour is required"),
        gate_colour: Yup.string().required("Gate Colour is required"),
      }),
    [],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (isQuoteMode) {
        if (!quotationId) return;
        setSaving(true);

        const payload = {
          quote_id: quotationId,
          step_no: "mezzanine-floor",
          last_completed_step: "mezzanine-floor",
          force_update: false,
          mezzanine_floor: {
            square_metreage: Number(values.square_metreage),
            ffl: Number(values.ffl),
            staircase_type: values.staircase_type || null,
            staircase_type_qty: Number(values.staircase_type_qty || 1),
            pallet_gate_type: values.pallet_gate_type || null,
            pallet_gate_type_qty: Number(values.pallet_gate_type_qty || 1),
            total_length_of_handrail: Number(values.total_length_of_handrail),
            floor_colour: values.floor_colour || null,
            gate_colour: values.gate_colour || null,
          },
        };

        dispatch(
          updateQuotationAPI({
            payload,
            onSuccess: (data: any) => {
              const updatedData = { ...data };
              if (updatedData?.mezzanine_floor) {
                updatedData.mezzanine_floor = {
                  ...updatedData.mezzanine_floor,
                  staircase_type_qty: Number(values.staircase_type_qty || 1),
                  pallet_gate_type_qty: Number(values.pallet_gate_type_qty || 1),
                };
              }
              dispatch(setQuotationDetails(updatedData));
              setSaving(false);

              if (onSaveAndNext) {
                onSaveAndNext();
                return;
              }

              const currentIdx = stepPlaylist.indexOf(activeStep);
              const nextStepKey =
                currentIdx !== -1 && currentIdx < stepPlaylist.length - 1
                  ? stepPlaylist[currentIdx + 1]
                  : CreateQuotationStepsEnum.SERVICES;

              const params: any = {
                step: nextStepKey,
              };
              if (quotationId) params.quotationId = quotationId;
              const mode = searchParams.get("mode");
              if (mode) params.mode = mode;
              setSearchParams(params);
            },
            onError: () => {
              setSaving(false);
            },
          }),
        );
        return;
      }

      // QR mode: no estimationId and no quotationId, but onSaveAndNext provided
      if (!estimationId) {
        if (onSaveAndNext) {
          onSaveAndNext(values);
        }
        return;
      }
      setSaving(true);

      const payload = {
        estimation_id: estimationId,
        step_no: "mezzanine-floor",
        mezzanine_floor: {
          square_metreage: Number(values.square_metreage),
          ffl: Number(values.ffl),
          staircase_type: values.staircase_type || null,
          staircase_type_qty: Number(values.staircase_type_qty || 1),
          pallet_gate_type: values.pallet_gate_type || null,
          pallet_gate_type_qty: Number(values.pallet_gate_type_qty || 1),
          total_length_of_handrail: Number(values.total_length_of_handrail),
          floor_colour: values.floor_colour || null,
          gate_colour: values.gate_colour || null,
        },
      };

      dispatch(
        updateEstimationAPI({
          payload,
          onSuccess: (data) => {
            const updatedData = { ...data };
            if (updatedData?.mezzanine_floor) {
              updatedData.mezzanine_floor = {
                ...updatedData.mezzanine_floor,
                staircase_type_qty: Number(values.staircase_type_qty || 1),
                pallet_gate_type_qty: Number(values.pallet_gate_type_qty || 1),
              };
            }
            dispatch(setEstimationDetails(updatedData));
            setSaving(false);

            if (onSaveAndNext) {
              onSaveAndNext();
              return;
            }

            // Navigate to next step in stepPlaylist
            const freshPlaylist = calculateEstimationStepPlaylist(
              data?.categories_snapshot || []
            );
            const currentIdx = freshPlaylist.indexOf(activeStep);
            const nextStepKey =
              currentIdx !== -1
                ? freshPlaylist[currentIdx + 1]
                : CreateEstimationStepsEnum.SERVICES;

            const params: any = {
              step: nextStepKey,
              estimationId: estimationId,
            };
            const mode = searchParams.get("mode");
            if (mode) params.mode = mode;
            setSearchParams(params);
          },
          onError: () => {
            setSaving(false);
          },
        }),
      );
    },
  });

  const {
    square_metreage,
    ffl,
    staircase_type,
    pallet_gate_type,
    total_length_of_handrail,
    floor_colour,
    gate_colour,
  } = formik.values;

  const allFieldsFilled = useMemo(() => {
    return (
      square_metreage !== "" &&
      square_metreage !== null &&
      ffl !== "" &&
      ffl !== null &&
      staircase_type !== undefined &&
      staircase_type !== null &&
      staircase_type !== "" &&
      pallet_gate_type !== undefined &&
      pallet_gate_type !== null &&
      pallet_gate_type !== "" &&
      total_length_of_handrail !== "" &&
      total_length_of_handrail !== null &&
      floor_colour !== undefined &&
      floor_colour !== null &&
      floor_colour !== "" &&
      gate_colour !== undefined &&
      gate_colour !== null &&
      gate_colour !== ""
    );
  }, [
    square_metreage,
    ffl,
    staircase_type,
    pallet_gate_type,
    total_length_of_handrail,
    floor_colour,
    gate_colour,
  ]);

  useEffect(() => {
    if (isQuoteMode && hasManufacturerPrice) {
      setLoadingCost(false);
      if (
        estimationDetails?.mezzanine_floor?.total_cost !== undefined &&
        estimationDetails?.mezzanine_floor?.total_cost !== null
      ) {
        setTotalCost(estimationDetails.mezzanine_floor.total_cost);
      }
      return;
    }

    if (allFieldsFilled) {
      const payload = {
        mezzanine_floor: {
          square_metreage: Number(square_metreage),
          ffl: Number(ffl),
          staircase_type,
          staircase_type_qty: Number(formik.values.staircase_type_qty || 1),
          pallet_gate_type,
          pallet_gate_type_qty: Number(formik.values.pallet_gate_type_qty || 1),
          total_length_of_handrail: Number(total_length_of_handrail),
          floor_colour,
          gate_colour,
        },
      };

      setLoadingCost(true);
      dispatch(
        calculateFloorCostAPI({
          payload,
          onSuccess: (data: any) => {
            setTotalCost(data?.total_cost ?? null);
            setLoadingCost(false);
          },
          onError: () => {
            setTotalCost(null);
            setLoadingCost(false);
          },
        }),
      );
    } else {
      setTotalCost(null);
    }
  }, [
    isQuoteMode,
    hasManufacturerPrice,
    allFieldsFilled,
    square_metreage,
    ffl,
    staircase_type,
    formik.values.staircase_type_qty,
    pallet_gate_type,
    formik.values.pallet_gate_type_qty,
    total_length_of_handrail,
    floor_colour,
    gate_colour,
    estimationDetails?.mezzanine_floor?.total_cost,
    dispatch,
  ]);

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
      return;
    }
    const currentIdx = stepPlaylist.indexOf(activeStep);
    const prevStepKey =
      stepPlaylist[currentIdx - 1] ||
      (isQuoteMode
        ? CreateQuotationStepsEnum.STORAGE_TYPES
        : CreateEstimationStepsEnum.STORAGE_TYPE);

    const params: any = {
      step: prevStepKey,
    };
    if (estimationId) params.estimationId = estimationId;
    if (quotationId) params.quotationId = quotationId;
    const mode = searchParams.get("mode");
    if (mode) params.mode = mode;
    setSearchParams(params);
  };

  const backupFloor = estimationDetails?.backup_mezzanine_floor;
  const hasBackupFloor = Boolean(
    backupFloor && typeof backupFloor === "object" && Object.keys(backupFloor).length > 0
  );
  const shouldShowBackupButton =
    hasBackupFloor && isPendingManufacturerQuoteOrHigher(estimationDetails?.status);

  const backupFloorModalJsx = (
    <Modal
      open={isBackupFloorModalOpen}
      centered
      width={"52rem"}
      footer={null}
      onCancel={() => setIsBackupFloorModalOpen(false)}
      closeIcon={svg?.closeIcon}
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          Previous Mezzanine Floor Details
        </div>
      }
      className="commonModalClass"
    >
      <div className="d-flex flex-column gap-3 pt-2">
        <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-light">
          <div className="row gy-3">
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Floor Square Metreage
              </label>
              <span className="fw-semibold text-primary fs-6">
                {backupFloor?.square_metreage
                  ? `${backupFloor.square_metreage} m²`
                  : "-"}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Finish Floor Level
              </label>
              <span className="fw-semibold text-primary fs-6">
                {backupFloor?.ffl ? `${backupFloor.ffl} mm` : "-"}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Decking
              </label>
              <span className="fw-semibold text-primary fs-6">
                Chipboard
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Staircase Type
              </label>
              <span className="fw-semibold text-primary fs-6">
                {getStaircaseName(backupFloor?.staircase_type)}
                {backupFloor?.staircase_type &&
                  ` (x${backupFloor?.staircase_type_qty || 1})`}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Pallet Gate Type
              </label>
              <span className="fw-semibold text-primary fs-6">
                {getPalletGateName(backupFloor?.pallet_gate_type)}
                {backupFloor?.pallet_gate_type &&
                  ` (x${backupFloor?.pallet_gate_type_qty || 1})`}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Total Length of Handrail
              </label>
              <span className="fw-semibold text-primary fs-6">
                {backupFloor?.total_length_of_handrail
                  ? `${backupFloor.total_length_of_handrail} mm`
                  : "-"}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Floor Colour
              </label>
              <span className="fw-semibold text-primary fs-6">
                {getFloorColourName(backupFloor?.floor_colour)}
              </span>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Gate Colour
              </label>
              <span className="fw-semibold text-primary fs-6">
                {getGateColourName(backupFloor?.gate_colour)}
              </span>
            </div>
          </div>
        </div>

        {backupFloor?.total_cost !== undefined && backupFloor?.total_cost !== null && (
          <div className="fs-5 text-dark text-center my-2">
            Previous Floor Details Total Amount:{" "}
            <span className="fw-bold">
              {formatCurrency(
                backupFloor.total_cost,
                estimationDetails?.currency || "GBP",
                estimationDetails?.conversion_rate
              )}
            </span>
          </div>
        )}

        <div className="d-flex justify-content-center gap-3 mt-4">
          <Button
            onClick={() => setIsBackupFloorModalOpen(false)}
            className="btn btn-lg btn-outline-secondary h-auto px-5 text-uppercase"
          >
            CLOSE
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (loadingSettings) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spin size="large" tip="Loading Floor Settings..." />
      </div>
    );
  }

  if (readOnly) {
    const floor = estimationDetails?.mezzanine_floor || {};
    return (
      <>
        <div className="card border-0 rounded-4 mb-md-4 mb-3 animate-fade-in">
          <div className="card-body p-md-4 p-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
              <h4 className="fs-4 fw-bold text-primary mb-0">Floor Details</h4>
              {!isQuoteMode && floor.total_cost !== undefined && floor.total_cost !== null && (
                <div className="fs-4 fs-4-mobile ms-auto">
                  {isEstimation ? "Estimated Floor Details Total Amount:" : "Floor Details Total Amount:"}{" "}
                  <span className="fw-bold">
                    {formatCurrency(
                      floor.total_cost,
                      estimationDetails?.currency || "GBP",
                      estimationDetails?.conversion_rate
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className="row gy-3">
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Floor Square Metreage
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {floor.square_metreage
                    ? `${floor.square_metreage} m²`
                    : "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Finish Floor Level
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {floor.ffl ? `${floor.ffl} mm` : "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Decking
                </label>
                <span className="fw-semibold text-primary fs-6">
                  Chipboard
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Staircase Type
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {getStaircaseName(floor.staircase_type)}
                  {floor.staircase_type && ` (x${floor.staircase_type_qty || 1})`}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Pallet Gate Type
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {getPalletGateName(floor.pallet_gate_type)}
                  {floor.pallet_gate_type && ` (x${floor.pallet_gate_type_qty || 1})`}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Total Length of Handrail
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {floor.total_length_of_handrail
                    ? `${floor.total_length_of_handrail} mm`
                    : "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Floor Colour
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {getFloorColourName(floor.floor_colour)}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  Gate Colour
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {getGateColourName(floor.gate_colour)}
                </span>
              </div>
            </div>

          </div>
        </div>

        {shouldShowBackupButton && (
          <div className="d-flex justify-content-center my-3">
            <span
              role="button"
              tabIndex={0}
              className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
              onClick={() => setIsBackupFloorModalOpen(true)}
            >
              View Previous Mezzanine Floor Details
            </span>
          </div>
        )}

        {backupFloorModalJsx}
      </>
    );
  }


  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-md-3 mb-2 gap-2 animate-fade-in">
        <h3 className="fs-4 fs-4-mobile fw-bold text-black mb-0">
          Floor Details
        </h3>
        {(totalCost !== null || loadingCost) && (
          <div className="fs-4 fs-4-mobile ms-auto">
            {isEstimation ? "Estimated Floor Details Total Amount:" : "Floor Details Total Amount:"}{" "}
            {loadingCost ? (
              <Spin size="small" className="ms-1" />
            ) : (
              <span className="fw-bold">
                {formatCurrency(
                  totalCost || 0,
                  estimationDetails?.currency || "GBP",
                  estimationDetails?.conversion_rate
                )}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="card border-0 rounded-4 mb-md-4 mb-3">
        <div className="card-body p-md-4 p-3">
          <form className="page-form" onSubmit={formik.handleSubmit}>
            <div className="row gy-md-3 gy-1 align-items-start">
              {/* Floor Square Metreage */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">
                    Floor Square Metreage (m²) <RedStar />
                  </label>
                  <Input
                    id="square_metreage"
                    name="square_metreage"
                    type="number"
                    placeholder="e.g. 150"
                    className="form-control form-control-lg"
                    status={
                      formik.errors.square_metreage &&
                      formik.touched.square_metreage
                        ? "error"
                        : ""
                    }
                    prefix={<span className="text-white">m²</span>}
                    value={formik.values.square_metreage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.square_metreage &&
                    formik.touched.square_metreage && (
                      <div className="text-danger fs-8">
                        {formik.errors.square_metreage as string}
                      </div>
                    )}
                </div>
              </div>

              {/* Finish Floor Level */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">
                    Finish Floor Level (mm) <RedStar />
                  </label>
                  <Input
                    id="ffl"
                    name="ffl"
                    type="number"
                    placeholder="e.g. 3000"
                    className="form-control form-control-lg"
                    status={
                      formik.errors.ffl && formik.touched.ffl ? "error" : ""
                    }
                    prefix={<span className="text-white">mm</span>}
                    value={formik.values.ffl}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.ffl && formik.touched.ffl && (
                    <div className="text-danger fs-8">
                      {formik.errors.ffl as string}
                    </div>
                  )}
                </div>
              </div>

              {/* Decking - Read-only Chipboard */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">Decking</label>
                  <Input
                    id="decking"
                    value="Chipboard"
                    disabled
                    className="form-control form-control-lg bg-light text-secondary"
                  />
                </div>
              </div>

              {/* Staircase Type Dropdown */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="custom-label mb-0 ff">Staircase Type <RedStar /></label>
                    {formik.values.staircase_type && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2 text-decoration-none fs-7 fw-semibold text-primary d-inline-flex align-items-center gap-1"
                        onClick={() => handleOpenQuantityModal("staircase_type", formik.values.staircase_type, true)}
                      >
                        <EditOutlined style={{ fontSize: "12px" }} />
                        <span>Qty: x{formik.values.staircase_type_qty || 1}</span>
                      </button>
                    )}
                  </div>
                  <Select
                    id="staircase_type"
                    className="w-100 auto-height-select"
                    popupClassName="full-text-select-dropdown"
                    placeholder="Select Staircase"
                    status={
                      formik.errors.staircase_type && formik.touched.staircase_type
                        ? "error"
                        : ""
                    }
                    value={
                      formik.values.staircase_type === ""
                        ? null
                        : formik.values.staircase_type
                    }
                    onSelect={(value: string) => {
                      handleOpenQuantityModal("staircase_type", value);
                    }}
                    onChange={(value) => {
                      formik.setFieldValue("staircase_type", value || undefined);
                      if (!value) {
                        formik.setFieldValue("staircase_type_qty", 1);
                        formik.setFieldTouched("staircase_type", true);
                      }
                    }}
                    allowClear
                    suffixIcon={svg?.selectDownArrow}
                    onBlur={() => formik.setFieldTouched("staircase_type", true)}
                    labelRender={({ label }) => (
                      <span>
                        {label}
                        {formik.values.staircase_type ? ` (x${formik.values.staircase_type_qty || 1})` : ""}
                      </span>
                    )}
                    options={
                      masterSettings?.stairs?.map((stair: any) => ({
                        label: `${stair.name}`,
                        value: stair._id,
                      })) || []
                    }
                  />
                  {formik.errors.staircase_type && formik.touched.staircase_type && (
                    <div className="text-danger fs-8">{formik.errors.staircase_type as string}</div>
                  )}
                </div>
              </div>

              {/* Pallet Gate Type Dropdown */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="custom-label mb-0 ff">
                      Pallet Gate Type <RedStar />
                    </label>
                    {formik.values.pallet_gate_type && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2 text-decoration-none fs-7 fw-semibold text-primary d-inline-flex align-items-center gap-1"
                        onClick={() => handleOpenQuantityModal("pallet_gate_type", formik.values.pallet_gate_type, true)}
                      >
                        <EditOutlined style={{ fontSize: "12px" }} />
                        <span>Qty: x{formik.values.pallet_gate_type_qty || 1}</span>
                      </button>
                    )}
                  </div>
                  <Select
                    id="pallet_gate_type"
                    className="w-100 auto-height-select"
                    popupClassName="full-text-select-dropdown"
                    placeholder="Select Pallet Gate"
                    status={
                      formik.errors.pallet_gate_type && formik.touched.pallet_gate_type
                        ? "error"
                        : ""
                    }
                    value={
                      formik.values.pallet_gate_type === ""
                        ? null
                        : formik.values.pallet_gate_type
                    }
                    onSelect={(value: string) => {
                      handleOpenQuantityModal("pallet_gate_type", value);
                    }}
                    onChange={(value) => {
                      formik.setFieldValue("pallet_gate_type", value || undefined);
                      if (!value) {
                        formik.setFieldValue("pallet_gate_type_qty", 1);
                        formik.setFieldTouched("pallet_gate_type", true);
                      }
                    }}
                    allowClear
                    suffixIcon={svg?.selectDownArrow}
                    onBlur={() => formik.setFieldTouched("pallet_gate_type", true)}
                    labelRender={({ label }) => (
                      <span>
                        {label}
                        {formik.values.pallet_gate_type ? ` (x${formik.values.pallet_gate_type_qty || 1})` : ""}
                      </span>
                    )}
                    options={
                      masterSettings?.pallet_gates?.map((gate: any) => ({
                        label: `${gate.name}`,
                        value: gate._id,
                      })) || []
                    }
                  />
                  {formik.errors.pallet_gate_type && formik.touched.pallet_gate_type && (
                    <div className="text-danger fs-8">{formik.errors.pallet_gate_type as string}</div>
                  )}
                </div>
              </div>

              {/* Total Length of Handrail */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">
                    Total Length of Handrail (mm) <RedStar />
                  </label>
                  <Input
                    id="total_length_of_handrail"
                    name="total_length_of_handrail"
                    type="number"
                    placeholder="e.g. 5000"
                    className="form-control form-control-lg"
                    status={
                      formik.errors.total_length_of_handrail &&
                      formik.touched.total_length_of_handrail
                        ? "error"
                        : ""
                    }
                    prefix={<span className="text-white">mm</span>}
                    value={formik.values.total_length_of_handrail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.total_length_of_handrail &&
                    formik.touched.total_length_of_handrail && (
                      <div className="text-danger fs-8">
                        {formik.errors.total_length_of_handrail as string}
                      </div>
                    )}
                </div>
              </div>

              {/* Floor Colour Dropdown */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">Floor Colour <RedStar /></label>
                  <Select
                    id="floor_colour"
                    className="w-100 auto-height-select"
                    popupClassName="full-text-select-dropdown"
                    placeholder="Select Floor Colour"
                    status={formik.errors.floor_colour && formik.touched.floor_colour ? "error" : ""}
                    value={
                      formik.values.floor_colour === ""
                        ? null
                        : formik.values.floor_colour
                    }
                    onChange={(value) => {
                      formik.setFieldValue("floor_colour", value);
                      formik.setFieldTouched("floor_colour", true, false);
                      if (value) formik.setFieldError("floor_colour", undefined);
                    }}
                    allowClear
                    suffixIcon={svg?.selectDownArrow}
                    onBlur={() => formik.setFieldTouched("floor_colour", true)}
                    options={
                      masterSettings?.floor_color_config?.colors?.map(
                        (color: any) => ({
                          label: `${color.name}`,
                          value: color._id,
                        }),
                      ) || []
                    }
                  />
                  {formik.errors.floor_colour && formik.touched.floor_colour && (
                    <div className="text-danger fs-8">{formik.errors.floor_colour as string}</div>
                  )}
                </div>
              </div>

              {/* Gate Colour Dropdown */}
              <div className="col-md-3 col-12">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">Gate Colour <RedStar /></label>
                  <Select
                    id="gate_colour"
                    className="w-100 auto-height-select"
                    popupClassName="full-text-select-dropdown"
                    placeholder="Select Gate Colour"
                    status={formik.errors.gate_colour && formik.touched.gate_colour ? "error" : ""}
                    value={
                      formik.values.gate_colour === ""
                        ? null
                        : formik.values.gate_colour
                    }
                    onChange={(value) => {
                      formik.setFieldValue("gate_colour", value);
                      formik.setFieldTouched("gate_colour", true, false);
                      if (value) formik.setFieldError("gate_colour", undefined);
                    }}
                    allowClear
                    suffixIcon={svg?.selectDownArrow}
                    onBlur={() => formik.setFieldTouched("gate_colour", true)}
                    options={
                      masterSettings?.gate_color_config?.colors?.map(
                        (color: any) => ({
                          label: `${color.name}`,
                          value: color._id,
                        }),
                      ) || []
                    }
                  />
                  {formik.errors.gate_colour && formik.touched.gate_colour && (
                    <div className="text-danger fs-8">{formik.errors.gate_colour as string}</div>
                  )}
                </div>
              </div>
            </div>


            {/* Bottom Buttons */}
            <div className="row justify-content-center mt-4">
              <div className="col-sm-auto col-6">
                <Button
                  className="btn btn-lg btn-outline-secondary px-4 w-100 h-auto text-uppercase"
                  onClick={handlePrevious}
                >
                  Previous
                </Button>
              </div>
              <div className="col-sm-auto col-6">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="btn btn-lg btn-primary px-4 h-auto w-100 text-uppercase"
                  loading={saving}
                >
                  Save & Next
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {shouldShowBackupButton && (
        <div className="d-flex justify-content-center my-3">
          <span
            role="button"
            tabIndex={0}
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => setIsBackupFloorModalOpen(true)}
          >
            View Previous Mezzanine Floor Details
          </span>
        </div>
      )}

      {backupFloorModalJsx}

      <Modal
        open={quantityModal.open}
        centered
        width={"33.75rem"}
        footer={null}
        onCancel={handleCancelQuantityModal}
        closeIcon={svg?.closeIcon}
        title={
          <div className="text-center fs-4 fw-bolder lh-m">
            {quantityModal.title}
          </div>
        }
        className="commonModalClass"
      >
        <div className="d-flex flex-column gap-3 pt-2">
          <div className="text-center">
            <label className="custom-label mb-1 ff d-block text-muted fs-6">Selected Item</label>
            <h6 className="fw-bold fs-5 text-dark mb-0">{quantityModal.itemName}</h6>
          </div>

          <div className="d-flex flex-column align-items-center gap-2 my-2">
            <label className="custom-label mb-1 ff fs-6">Quantity</label>
            <div
              className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput sm-width-mob"
              style={{ width: "10rem" }}
            >
              <Button
                type="text"
                icon={<MinusOutlined />}
                disabled={quantityModal.qty <= 1}
                className="btn btn-primary addRemoveBtn"
                onClick={() =>
                  setQuantityModal((prev) => ({
                    ...prev,
                    qty: Math.max(1, prev.qty - 1),
                  }))
                }
              />
              <InputNumber
                min={1}
                precision={0}
                step={1}
                value={quantityModal.qty}
                className="border-0 text-center fw-bold shadow-none custom-no-controls w-100"
                controls={false}
                onChange={(val) => {
                  if (val !== null && val !== undefined) {
                    const parsed = Math.floor(Number(val));
                    setQuantityModal((prev) => ({
                      ...prev,
                      qty: parsed > 0 ? parsed : 1,
                    }));
                  }
                }}
              />
              <Button
                type="text"
                icon={<PlusOutlined />}
                className="btn btn-primary addRemoveBtn"
                onClick={() =>
                  setQuantityModal((prev) => ({
                    ...prev,
                    qty: prev.qty + 1,
                  }))
                }
              />
            </div>
          </div>

          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              onClick={handleCancelQuantityModal}
              className="btn btn-lg btn-outline-secondary h-auto px-5 text-uppercase"
            >
              CANCEL
            </Button>

            <Button
              onClick={handleConfirmQuantityModal}
              className="btn btn-lg btn-primary h-auto px-5 text-uppercase"
            >
              CONFIRM
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FloorDetails;
